/**
 * StorageMeter
 * Compact storage usage meter — green / amber / red bands.
 * Polls /api/tenant/storage-usage. Hides itself if `quota_bytes` is 0.
 *
 * Drop-in usage:
 *   <StorageMeter compact/>          // small bar with tooltip
 *   <StorageMeter/>                  // full card (label + bar + CTA)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

export default function StorageMeter({ compact = false }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        api.get("/tenant/storage-usage")
            .then(r => alive && setData(r.data))
            .catch(() => alive && setData(null));
        return () => { alive = false; };
    }, []);

    if (!data || !data.quota_bytes) return null;
    const pct = Math.min(100, data.percent_used);
    const tone =
        pct >= 95 ? "danger" :
        pct >= 85 ? "warning" :
        pct >= 70 ? "amber" :
        "primary";
    const bgClass = {
        danger:  "bg-rose-500",
        warning: "bg-amber-500",
        amber:   "bg-yellow-500",
        primary: "bg-primary",
    }[tone];
    const label = `${data.used_gb} / ${data.quota_gb} GB`;
    const showAddonCta = pct >= 70;

    if (compact) {
        return (
            <div className="inline-flex items-center gap-2" title={label} data-testid="storage-meter-compact">
                <Database className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5}/>
                <div className="w-24 h-1.5 bg-muted rounded overflow-hidden">
                    <div className={`h-full ${bgClass} transition-all`} style={{ width: `${pct}%` }} data-testid="storage-meter-bar"/>
                </div>
                <span className="text-[11px] font-mono text-muted-foreground">{label}</span>
            </div>
        );
    }

    return (
        <div className="bg-muted border border-border p-4 rounded-lg" data-testid="storage-meter-full">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                    <div className="overline">Storage</div>
                </div>
                <span className="text-xs font-mono text-muted-foreground" data-testid="storage-meter-label">{label}</span>
            </div>
            <div className="h-2 bg-background border border-border rounded overflow-hidden mb-2">
                <div className={`h-full ${bgClass} transition-all`} style={{ width: `${pct}%` }}/>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{pct}% used{data.addon_units > 0 ? ` · including ${data.addon_units} × +25 GB add-on` : ""}</span>
                {showAddonCta && (
                    <Link to="/app/addons" data-testid="storage-meter-addon-cta"
                          className={`inline-flex items-center gap-1 hover:underline ${tone === "danger" ? "text-rose-600" : "text-primary"}`}>
                        {tone === "danger" && <AlertTriangle className="w-3 h-3" strokeWidth={1.75}/>}
                        Add +25 GB (₹149 / mo)
                    </Link>
                )}
            </div>
        </div>
    );
}
